"use strict";
// meetmate 팝업 ↔ mimac LMS postMessage 신뢰 검증(3중 레이어).
//
// 배경: meetmate 발신부는 targetOrigin '*' 로 보내므로 수신측(LMS)이 출처를 검증해야 한다.
// 기존에는 환경별 도메인 하드코딩(명시 목록 + 서픽스)에만 의존해, 새 배포 환경마다
// 코드 수정이 필요했고 누락 시 성적표가 조용히 무한 로딩되는 문제가 있었다.
//
// 레이어별 목적:
//   1) 팝업 신원(isMeetmatePopupSource): 우리가 window.open 으로 직접 연 팝업인지
//      e.source 동일성으로 확인 — 도메인과 무관하게 어떤 환경에서도 동작(환경 무관 신원).
//   2) opener 역참조(isOpenedByThisWindow): e.source.opener === window — "그 창을 연 것이
//      이 창인가"를 브라우저가 보증하는 신원으로 확인. 레이어 1 의 WeakSet 은 모듈 상태라
//      LMS 페이지 새로고침 시 소실되지만, 브라우징 컨텍스트 관계인 opener 는 살아남는다.
//      (도메인 휴리스틱이 아니므로 공용 서픽스(co.kr 등) 판별 문제가 원천적으로 없다)
//   3) 명시 목록 + 알려진 서픽스(isTrustedMeetmateOrigin): 후방 안전망.
//      팝업이 아닌 경로가 생기거나 opener 가 끊긴(noopener 등) 예외 상황의 하위 호환 보장.
//
// 새 배포 환경 추가 시에도 레이어 1·2(신원 기반, 도메인 무관)로 무설정 동작한다.
Object.defineProperty(exports, "__esModule", { value: true });
exports.isTrustedMeetmateMessage = exports.isTrustedMeetmateOrigin = exports.isMeetmatePopupSource = exports.registerMeetmatePopup = void 0;
// 레이어 1: goToConference 가 연 meetmate 팝업의 WindowProxy 를 기억한다.
// WeakSet 이므로 팝업이 닫혀 GC 되면 자동으로 정리된다.
const openedMeetmatePopups = new WeakSet();
// window.open 성공 직후 호출 — 이후 이 팝업에서 오는 메시지는 도메인과 무관하게 신뢰.
const registerMeetmatePopup = (w) => {
    if (w)
        openedMeetmatePopups.add(w);
};
exports.registerMeetmatePopup = registerMeetmatePopup;
// e.source 가 우리가 연 meetmate 팝업인지 확인.
// WindowProxy 는 브라우징 컨텍스트 단위로 동일성이 유지되므로,
// attend-page(LMS 도메인) → meetmate 도메인으로 리다이렉트된 뒤에도
// e.source === 등록해 둔 WindowProxy 비교(WeakSet.has)가 성립한다.
const isMeetmatePopupSource = (source) => {
    try {
        return !!source && openedMeetmatePopups.has(source);
    }
    catch {
        // cross-origin WindowProxy 접근 예외 등은 불신 처리
        return false;
    }
};
exports.isMeetmatePopupSource = isMeetmatePopupSource;
// 레이어 2: e.source 창의 opener 가 이 창인지 확인 — "그 창을 연 것이 나"라는 신원.
// opener 는 HTML 스펙상 cross-origin WindowProxy 에서도 접근이 허용되는 속성이고,
// 브라우징 컨텍스트 관계라서 LMS 페이지가 새로고침돼도(모듈 WeakSet 소실) 유지된다.
// 팝업이 attend-page(LMS) → meetmate 도메인으로 리다이렉트돼도 관계는 그대로다.
// 제3의 창은 자신의 opener 를 이 창으로 위장할 수 없으므로(브라우저가 설정) 스푸핑 불가.
// 주의: 이 창이 연 다른 팝업(예: 라이브 재생 창)도 통과하지만, 모두 우리 서비스가
// 항해시킨 창이므로 임의 외부 출처 차단이라는 목적에는 부합한다.
const isOpenedByThisWindow = (source) => {
    try {
        return !!source && source.opener === window;
    }
    catch {
        // MessagePort/ServiceWorker 등 opener 개념이 없는 소스, 접근 예외는 불신 처리
        return false;
    }
};
// 레이어 3: 명시 목록 + 알려진 서픽스(후방 안전망/로컬 개발용).
// 기존 MainClasses.tsx 의 검증을 그대로 이전 — 기존 통과 오리진은 전부 계속 통과한다.
const MEETMATE_TRUSTED_ORIGINS = [
    'https://mimac-meet.dev.meetmate.co.kr',
    'https://mimac-meet.stage.meetmate.co.kr',
    'https://mimac-meet.meetmate.co.kr',
    'https://meet.querensys.com',
    'https://meet.klassmate.co.kr', // 운영
];
const isTrustedMeetmateOrigin = (origin) => {
    if (!origin)
        return false;
    if (MEETMATE_TRUSTED_ORIGINS.includes(origin))
        return true;
    try {
        const { protocol, hostname } = new URL(origin);
        if (protocol !== 'https:')
            return false;
        return (hostname === 'meetmate.co.kr' ||
            hostname.endsWith('.meetmate.co.kr') ||
            hostname === 'meet.querensys.com' ||
            hostname.endsWith('.querensys.com') ||
            hostname.endsWith('.klassmate.co.kr'));
    }
    catch {
        return false;
    }
};
exports.isTrustedMeetmateOrigin = isTrustedMeetmateOrigin;
// 통합 진입점: 레이어 1(등록된 팝업) → 2(opener 역참조) → 3(명시/서픽스) 순으로
// 하나라도 통과하면 신뢰. 새 환경에서도 1·2 레이어(신원 기반) 덕에 코드 수정 없이 동작한다.
const isTrustedMeetmateMessage = (e) => (0, exports.isMeetmatePopupSource)(e.source) || isOpenedByThisWindow(e.source) || (0, exports.isTrustedMeetmateOrigin)(e.origin);
exports.isTrustedMeetmateMessage = isTrustedMeetmateMessage;
